import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Info from "@insite/mobius/Icons/Info";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TokenExFrame, { TokenExFramePresentationProps } from "@insite/mobius/TokenExFrame";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AmericanExpress from "@SupplyLand/Icons/AmericanExpress";
import MasterCard from "@SupplyLand/Icons/MasterCard";
import Visa from "@SupplyLand/Icons/Visa";
import React, { useState } from "react";
import styled, { css } from "styled-components";

interface OwnProps {
    canSaveCard?: boolean;
    /** @deprecated Specify the "TokenEx" value for the `iframe` property instead. */
    useTokenExGateway?: boolean;
    iframe?: "TokenEx" | "Paymetric";
    paymetricFrameRef?: React.Ref<HTMLIFrameElement>;
    isTokenExIframeLoaded?: boolean;
    saveCard: boolean;
    makeDefaultCard: boolean;
    onSaveCardChange: (_: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    onMakeDefaultCardChange: (_: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    cardHolderName: string;
    cardHolderNameRef?: React.Ref<HTMLInputElement>;
    onCardHolderNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    cardHolderNameError?: string;
    cardNumber: string;
    cardNumberRef?: React.Ref<HTMLInputElement>;
    onCardNumberChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    cardNumberError?: string;
    cardType: string;
    cardTypeRef?: React.Ref<HTMLSelectElement>;
    possibleCardType: string;
    onCardTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    cardTypeError?: string;
    expirationMonth: number;
    expirationMonthRef?: React.Ref<HTMLSelectElement>;
    onExpirationMonthChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    expirationError?: string;
    expirationYear: number;
    expirationYearRef?: React.Ref<HTMLSelectElement>;
    onExpirationYearChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    securityCode: string;
    securityCodeRef?: React.Ref<HTMLInputElement>;
    onSecurityCodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    securityCodeError?: string;
    availableCardTypes: { key: string; value: string }[];
    availableMonths: { key: string; value: number }[];
    availableYears: { key: number; value: number }[];
    extendedStyles?: CreditCardDetailsEntryStyles;
}

export interface CreditCardDetailsEntryStyles {
    creditCardContainer?: GridContainerProps;
    creditCardDetailsContainer?: GridContainerProps;
    creditCardDetailsHeadingGridItem?: GridItemProps;
    creditCardDetailsHeading?: TypographyPresentationProps;
    saveCardGridItem?: GridItemProps;
    saveCardCheckboxGroup?: CheckboxGroupComponentProps;
    saveCardCheckbox?: CheckboxPresentationProps;
    cardHolderNameGridItem?: GridItemProps;
    cardHolderNameText?: TextFieldPresentationProps;
    cardNumberGridItem?: GridItemProps;
    cardNumberTokenExFrameWrapper?: InjectableCss;
    cardNumberTokenExFrame?: TokenExFramePresentationProps;
    cardNumberText?: TextFieldPresentationProps;
    cardTypeGridItem?: GridItemProps;
    cardTypeTokenExLabel?: TypographyPresentationProps;
    cardTypeTokenExText?: TypographyPresentationProps;
    cardTypeSelect?: SelectPresentationProps;
    expirationDateGridItem?: GridItemProps;
    expirationDateContainer?: GridContainerProps;
    expirationMonthGridItem?: GridItemProps;
    expirationMonthSelect?: SelectPresentationProps;
    expirationYearGridItem?: GridItemProps;
    expirationYearSelect?: SelectPresentationProps;
    securityCodeGridItem?: GridItemProps;
    securityCodeTokenExFrameWrapper?: InjectableCss;
    securityCodeTokenExFrame?: TokenExFramePresentationProps;
    securityCodeText?: TextFieldPresentationProps;
    securityCodeHelpModal?: ModalPresentationProps;
    securityCodeHelpModalImageWrapper?: InjectableCss;
    securityCodeHelpImage?: LazyImageProps;
    paymetricGridItem?: GridItemProps;
    paymetricIframe?: InjectableCss;
    allowedCreditCardTypesIcon?: IconPresentationProps;
    allowedCreditCardTypesModal?: ModalPresentationProps;
    allowedCreditCardTypesTable?: DataTablePresentationProps;
    allowedCreditCardTypesTableBody?: DataTableBodyProps;
    allowedCreditCardTypesTableRow?: DataTableRowProps;
    allowedCreditCardTypesTableCell?: DataTableCellBaseProps;
    allowedCreditCardTypesGridContainer?: GridContainerProps;
    allowedCreditCardTypesGridItem?: GridItemProps;
}

export const creditCardDetailsEntryStyles: CreditCardDetailsEntryStyles = {
    allowedCreditCardTypesIcon: {
        color: "info",
        css: css`
            margin: 4px 0 1rem 10px;
        `,
    },
    allowedCreditCardTypesGridContainer: {
        gap: 0,
        css: css`
            margin-bottom: 25px;
        `,
    },
    allowedCreditCardTypesGridItem: {
        width: [4, 4, 4, 2, 2],
        css: css`
            flex-direction: row;
            justify-content: center;
        `,
    },
    expirationMonthSelect: {
        labelProps: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                    ])}
            `,
        },
    },
    expirationYearSelect: {
        labelProps: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                    ])}
            `,
        },
    },
    securityCodeTokenExFrame: {
        labelProps: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 14px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                        css`
                            font-size: 16px;
                        `,
                    ])}
            `,
        },
    },
    creditCardContainer: { gap: 10 },
    creditCardDetailsContainer: {
        gap: 10,
        css: css`
            box-shadow: 0px 0px 4px 2px rgb(0 0 0 / 20%);
            padding: 20px 20px 5px 20px;
            margin: 0px 5px 20px 5px;
        `,
    },
    creditCardDetailsHeadingGridItem: {
        width: 12,
        css: css``,
    },
    creditCardDetailsHeading: { variant: "h5" },
    saveCardGridItem: {
        width: 6,
        css: css`
            align-items: center;
        `,
    },
    cardHolderNameGridItem: { width: [12, 12, 12, 6, 6] },
    cardNumberGridItem: { width: [12, 12, 12, 6, 6] },
    cardNumberTokenExFrameWrapper: {
        css: css`
            height: inherit;
        `,
    },
    cardTypeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    cardTypeTokenExLabel: { weight: 600 },
    expirationDateGridItem: { width: 9 },
    expirationDateContainer: { gap: 10 },
    expirationMonthGridItem: { width: 6 },
    expirationYearGridItem: { width: 6 },
    securityCodeGridItem: { width: 3 },
    securityCodeTokenExFrameWrapper: {
        css: css`
            height: inherit;
        `,
    },
    securityCodeHelpModalImageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    paymetricGridItem: {
        width: 12,
    },
    paymetricIframe: {
        css: css`
            width: 100%;
        `,
    },
};

const CardNumberTokenExFrameWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;
const SecurityCodeTokenExFrameWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;
const SecurityCodeImageWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const PaymetricIframe = styled.iframe<InjectableCss>`
    ${({ css }) => css}
`;

const CustomCreditCardDetailsEntry = ({
    useTokenExGateway,
    iframe,
    paymetricFrameRef,
    isTokenExIframeLoaded,
    extendedStyles,
    canSaveCard,
    saveCard,
    makeDefaultCard,
    onSaveCardChange,
    onMakeDefaultCardChange,
    cardHolderName,
    cardHolderNameRef,
    onCardHolderNameChange,
    cardHolderNameError,
    cardNumberError,
    cardNumber,
    cardNumberRef,
    onCardNumberChange,
    possibleCardType,
    onCardTypeChange,
    cardTypeError,
    cardType,
    cardTypeRef,
    availableCardTypes,
    expirationMonth,
    expirationMonthRef,
    onExpirationMonthChange,
    expirationError,
    availableMonths,
    expirationYear,
    expirationYearRef,
    onExpirationYearChange,
    availableYears,
    securityCodeError,
    securityCode,
    securityCodeRef,
    onSecurityCodeChange,
}: OwnProps) => {
    const [isSecurityCodeModalOpen, setIsSecurityCodeModalOpen] = useState(false);
    const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);

    const [styles] = React.useState(() => mergeToNew(creditCardDetailsEntryStyles, extendedStyles));

    const handleSecurityCodeHelpLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setIsSecurityCodeModalOpen(true);
    };

    const handleSecurityCodeModalClose = (_: React.SyntheticEvent<Element, Event>) => setIsSecurityCodeModalOpen(false);

    const modalCloseHandler = () => {
        setInfoModalIsOpen(false);
    };

    const openAllowedCreditCardTypesModal = () => {
        setInfoModalIsOpen(true);
    };

    const securityCodeLabel = translate("");
    const securityCodeHint = <Link onClick={handleSecurityCodeHelpLinkClick}>{securityCodeLabel}</Link>;

    const resolvedShouldUseTokenEx = iframe === "TokenEx" || useTokenExGateway;

    cardType = GetCardType(possibleCardType);
    return (
        <GridContainer {...styles.creditCardContainer}>
            <GridItem {...styles.creditCardDetailsHeadingGridItem}>
                <Typography {...styles.creditCardDetailsHeading} as="h2">
                    {translate("Credit Card Details")}
                </Typography>
                <Icon
                    {...styles.allowedCreditCardTypesIcon}
                    width={20}
                    height={20}
                    src={Info}
                    onClick={openAllowedCreditCardTypesModal}
                />
                <Modal
                    {...styles.allowedCreditCardTypesModal}
                    headline={translate("Accepted Credit Card Types")}
                    isOpen={infoModalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    <DataTable {...styles.allowedCreditCardTypesTable}>
                        <DataTableBody {...styles.allowedCreditCardTypesTableBody}>
                            <DataTableRow {...styles.allowedCreditCardTypesTableRow}>
                                <DataTableCell {...styles.allowedCreditCardTypesTableCell}>
                                    <GridContainer {...styles.allowedCreditCardTypesGridContainer}>
                                        <GridItem {...styles.allowedCreditCardTypesGridItem}>
                                            <TokenExCardTypeDisplay
                                                {...styles.cardTypeTokenExText}
                                                possibleCardType="visa"
                                                aria-labelledby="cardTypeTokenEx"
                                            />
                                        </GridItem>
                                        <GridItem {...styles.allowedCreditCardTypesGridItem}>
                                            <TokenExCardTypeDisplay
                                                {...styles.cardTypeTokenExText}
                                                possibleCardType="masterCard"
                                                aria-labelledby="cardTypeTokenEx"
                                            />
                                        </GridItem>
                                        <GridItem {...styles.allowedCreditCardTypesGridItem}>
                                            <TokenExCardTypeDisplay
                                                {...styles.cardTypeTokenExText}
                                                possibleCardType="americanExpress"
                                                aria-labelledby="cardTypeTokenEx"
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </DataTableCell>
                            </DataTableRow>
                        </DataTableBody>
                    </DataTable>
                </Modal>
            </GridItem>
            <GridContainer {...styles.creditCardDetailsContainer}>
                {canSaveCard && (
                    <>
                        <GridItem {...styles.saveCardGridItem}>
                            <CheckboxGroup {...styles.saveCardCheckboxGroup}>
                                <Checkbox {...styles.saveCardCheckbox} checked={saveCard} onChange={onSaveCardChange}>
                                    {translate("Save card information")}
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                        <GridItem {...styles.saveCardGridItem}>
                            <CheckboxGroup {...styles.saveCardCheckboxGroup}>
                                <Checkbox
                                    {...styles.saveCardCheckbox}
                                    checked={makeDefaultCard}
                                    onChange={onMakeDefaultCardChange}
                                >
                                    {translate("Make Default")}
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                    </>
                )}
                {iframe === "Paymetric" ? (
                    <GridItem {...styles.paymetricGridItem}>
                        <PaymetricIframe id="paymetricIframe" ref={paymetricFrameRef} {...styles.paymetricIframe} />
                    </GridItem>
                ) : (
                    <>
                        <GridItem {...styles.cardHolderNameGridItem}>
                            <TextField
                                {...styles.cardHolderNameText}
                                label={translate("Name on Card")}
                                value={cardHolderName}
                                onChange={onCardHolderNameChange}
                                required
                                maxLength={30}
                                error={cardHolderNameError}
                                data-test-selector="checkoutReviewAndSubmit_cardHolderName"
                                ref={cardHolderNameRef}
                            />
                        </GridItem>
                        <GridItem {...styles.cardNumberGridItem}>
                            {resolvedShouldUseTokenEx ? (
                                <TokenExFrame
                                    {...styles.cardNumberTokenExFrame}
                                    label={translate("Card Number")}
                                    tokenExIFrameContainer={
                                        <CardNumberTokenExFrameWrapper
                                            {...styles.cardNumberTokenExFrameWrapper}
                                            id="tokenExCardNumber"
                                        />
                                    }
                                    disabled={!isTokenExIframeLoaded}
                                    required
                                    error={cardNumberError}
                                    data-test-selector="checkoutReviewAndSubmit_cardNumber"
                                />
                            ) : (
                                <TextField
                                    {...styles.cardNumberText}
                                    label={translate("Card Number")}
                                    value={cardNumber}
                                    onChange={onCardNumberChange}
                                    required
                                    maxLength={16}
                                    error={cardNumberError}
                                    data-test-selector="checkoutReviewAndSubmit_cardNumber"
                                    ref={cardNumberRef}
                                />
                            )}
                        </GridItem>
                        <GridItem {...styles.cardTypeGridItem}>
                            {resolvedShouldUseTokenEx ? (
                                <>
                                    <Typography {...styles.cardTypeTokenExLabel} id="cardTypeTokenEx">
                                        {translate("Card Type")}
                                    </Typography>
                                    <TokenExCardTypeDisplay
                                        {...styles.cardTypeTokenExText}
                                        possibleCardType={possibleCardType}
                                        aria-labelledby="cardTypeTokenEx"
                                    />
                                </>
                            ) : (
                                <Select
                                    {...styles.cardTypeSelect}
                                    label={translate("Card Type")}
                                    value={cardType}
                                    onChange={onCardTypeChange}
                                    required
                                    error={cardTypeError}
                                    data-test-selector="checkoutReviewAndSubmit_cardType"
                                    ref={cardTypeRef}
                                >
                                    <option value="">{translate("Select Card")}</option>
                                    {availableCardTypes.map(ct => (
                                        <option key={ct.value} value={ct.value}>
                                            {ct.key}
                                        </option>
                                    ))}
                                </Select>
                            )}
                        </GridItem>
                        <GridItem {...styles.expirationDateGridItem}>
                            <GridContainer {...styles.expirationDateContainer}>
                                <GridItem {...styles.expirationMonthGridItem}>
                                    <Select
                                        {...styles.expirationMonthSelect}
                                        label={translate("Exp. Month")}
                                        value={expirationMonth}
                                        onChange={onExpirationMonthChange}
                                        required
                                        error={expirationError}
                                        data-test-selector="checkoutReviewAndSubmit_expirationMonth"
                                        ref={expirationMonthRef}
                                    >
                                        {availableMonths.map(month => (
                                            <option key={month.value} value={month.value}>
                                                {month.key}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                                <GridItem {...styles.expirationYearGridItem}>
                                    <Select
                                        {...styles.expirationYearSelect}
                                        label={translate("Exp. Year")}
                                        value={expirationYear}
                                        onChange={onExpirationYearChange}
                                        required
                                        data-test-selector="checkoutReviewAndSubmit_expirationYear"
                                        ref={expirationYearRef}
                                    >
                                        {availableYears.map(year => (
                                            <option key={year.value} value={year.value}>
                                                {year.key}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.securityCodeGridItem}>
                            {resolvedShouldUseTokenEx ? (
                                <TokenExFrame
                                    {...styles.securityCodeTokenExFrame}
                                    label={translate("CVV")}
                                    hint={securityCodeHint}
                                    tokenExIFrameContainer={
                                        <SecurityCodeTokenExFrameWrapper
                                            {...styles.securityCodeTokenExFrameWrapper}
                                            id="tokenExSecurityCode"
                                        />
                                    }
                                    disabled={!isTokenExIframeLoaded}
                                    required
                                    error={securityCodeError}
                                    data-test-selector="checkoutReviewAndSubmit_securityCode"
                                />
                            ) : (
                                <TextField
                                    {...styles.securityCodeText}
                                    label={translate("Security code")}
                                    hint={securityCodeHint}
                                    value={securityCode}
                                    onChange={onSecurityCodeChange}
                                    required
                                    minLength={3}
                                    maxLength={4}
                                    error={securityCodeError}
                                    data-test-selector="checkoutReviewAndSubmit_securityCode"
                                    ref={securityCodeRef}
                                />
                            )}
                            <Modal
                                headline={securityCodeLabel}
                                isOpen={isSecurityCodeModalOpen}
                                handleClose={handleSecurityCodeModalClose}
                                {...styles.securityCodeHelpModal}
                            >
                                <SecurityCodeImageWrapper {...styles.securityCodeHelpModalImageWrapper}>
                                    <LazyImage
                                        src="/images/security_code_sample.jpg"
                                        imgProps={{ width: 660, height: 264 }}
                                        altText={translate("Location of security code on card")}
                                        {...styles.securityCodeHelpImage}
                                    />
                                </SecurityCodeImageWrapper>
                            </Modal>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        </GridContainer>
    );
};

type TokenExCardTypeDisplayProps = TypographyProps & {
    possibleCardType: string;
};

const TokenExCardTypeDisplay = ({ possibleCardType, ...otherProps }: TokenExCardTypeDisplayProps) => {
    if (possibleCardType === "masterCard") {
        return <Icon src={MasterCard} size={62} />;
    }
    if (possibleCardType === "americanExpress") {
        return <Icon src={AmericanExpress} size={62} />;
    }
    if (possibleCardType === "visa") {
        return <Icon src={Visa} size={62} />;
    }
    return <Typography {...otherProps}></Typography>;
};

const GetCardType = (possibleCardType: string) => {
    if (possibleCardType === "masterCard") {
        return "MASTERCARD";
    }
    if (possibleCardType === "americanExpress") {
        return "AMERICAN EXPRESS";
    }
    if (possibleCardType === "visa") {
        return "VISA";
    }
    return "";
};

export default CustomCreditCardDetailsEntry;
